import {
  Alert,
  Box,
  Flex,
  useToast as useChakraToast,
  type UseToastOptions,
} from '@chakra-ui/react';
import { useMemo } from 'react';

type Value = string | Partial<UseToastOptions>;

const getOptions = (value: Value): UseToastOptions => {
  return typeof value === 'string' ? { description: value } : value;
};

// Custom-render toast body shared by the variant-based helpers (`notify`,
// `purple`). Stacks title over description when both are present, otherwise
// renders the description alone. `descriptionColor` tunes the sub-text per
// variant (omit it to inherit the variant's own color).
const renderVariantAlert = (
  options: UseToastOptions,
  variant: string,
  descriptionColor?: string
) => {
  const { title, description } = options;
  return (
    <Alert variant={variant}>
      {title && description ? (
        <Flex align="stretch" direction="column" flexBasis="100%" gap="0.5">
          <Box>{title}</Box>
          <Box color={descriptionColor} fontSize="sm">
            {description}
          </Box>
        </Flex>
      ) : (
        description
      )}
    </Alert>
  );
};

const globalOptions: UseToastOptions = {
  position: 'top',
  isClosable: true,
};

export const useToast = () => {
  const toast = useChakraToast(globalOptions);

  return useMemo(
    () => ({
      toast,
      ...toast,

      success: (description: Value) => {
        const options = getOptions(description);
        return toast({ status: 'success', ...options });
      },

      error: (description: Value) => {
        const options = getOptions(description);
        return toast({ status: 'error', ...options });
      },

      warning: (description: Value) => {
        const options = getOptions(description);
        return toast({ status: 'warning', ...options });
      },

      info: (description: Value) => {
        const options = getOptions(description);
        return toast({ status: 'info', ...options });
      },

      notify: (description: Value) => {
        const options = getOptions(description);
        return toast({
          position: 'bottom-right',
          render: () => renderVariantAlert(options, 'notify', 'customgray.mid'),
          ...options,
        });
      },

      // Friendly branded notice on a deep purple background — softer than the
      // red `error` status for expected, non-alarming feedback.
      purple: (description: Value) => {
        const options = getOptions(description);
        return toast({
          render: () => renderVariantAlert(options, 'purple'),
          ...options,
        });
      },
    }),
    [toast]
  );
};
